import React, { useState, useEffect } from 'react';
import { QuestionMarkCircleIcon } from '../constants';

interface Section {
  id: string;
  title: string;
  content: string;
  subsections?: Section[];
}

// Helper function to create image HTML with actual screenshots or placeholders
const createImagePlaceholder = (alt: string, caption?: string): string => {
  // Map descriptions to actual screenshot files
  const imageMap: Record<string, string> = {
    'Dashboard Overview Screenshot': '/images/manual/dashboard-overview.png',
    'Full Dashboard View': '/images/manual/dashboard-overview.png',
    'Login Screen': '/images/manual/login-screen.png',
    'Login Error Message': '/images/manual/login-screen.png',
    'Successful Login Dashboard': '/images/manual/dashboard-overview.png',
    'Screen Layout with Labels': '/images/manual/dashboard-overview.png',
    'Stations Page Overview': '/images/manual/stations-overview.png',
    'Stations Management Page': '/images/manual/stations-overview.png',
    'Empty Station Row': '/images/manual/stations-overview.png',
    'Assignment Dialog Box': '/images/manual/stations-overview.png',
    'Employee List': '/images/manual/employees-page.png',
    'Employees Management': '/images/manual/employees-page.png',
    'Products Management': '/images/manual/products-page.png',
    'Production Reports': '/images/manual/reports-page.png',
    'System Settings': '/images/manual/settings-page.png',
    'User Manual': '/images/manual/user-manual.png',
    'Production Status Bar': '/images/manual/production-status-bar.png',
  };
  
  // Find matching image or use placeholder
  let imagePath = '';
  for (const [key, value] of Object.entries(imageMap)) {
    if (alt.includes(key)) {
      imagePath = value;
      break;
    }
  }
  
  if (imagePath) {
    // Return actual image
    return `
      <div class="my-8 mx-auto max-w-4xl">
        <div class="bg-white border-2 border-slate-300 rounded-xl shadow-lg overflow-hidden">
          <img src="${imagePath}" alt="${alt}" class="w-full h-auto" />
        </div>
        ${caption ? `<p class="text-sm text-indigo-600 text-center mt-3 font-medium bg-indigo-50 rounded-lg py-2 px-4 mx-auto max-w-2xl">ℹ️ ${caption}</p>` : ''}
      </div>
    `;
  } else {
    // Return placeholder for images we don't have
    return `
      <div class="my-8 mx-auto max-w-3xl">
        <div class="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-xl shadow-lg p-12 text-center relative overflow-hidden">
          <div class="absolute top-0 right-0 bg-indigo-500 text-white px-3 py-1 rounded-bl-lg text-xs font-semibold">
            SCREENSHOT NEEDED
          </div>
          <div class="relative z-10">
            <div class="bg-white rounded-full p-4 w-20 h-20 mx-auto mb-4 shadow-md">
              <svg class="w-12 h-12 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p class="text-lg font-semibold text-slate-700 mb-2">📸 ${alt}</p>
            <p class="text-sm text-slate-500">Additional screenshot needed</p>
          </div>
          <div class="absolute -bottom-6 -left-6 w-24 h-24 bg-indigo-100 rounded-full opacity-50"></div>
          <div class="absolute -top-6 -right-6 w-32 h-32 bg-blue-100 rounded-full opacity-50"></div>
        </div>
        ${caption ? `<p class="text-sm text-indigo-600 text-center mt-3 font-medium bg-indigo-50 rounded-lg py-2 px-4">ℹ️ ${caption}</p>` : ''}
      </div>
    `;
  }
};

const UserManualEnhanced: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('getting-started');
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['getting-started']));

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const manualContent: Record<string, Section> = {
    'getting-started': {
      id: 'getting-started',
      title: '🚀 Getting Started',
      content: '',
      subsections: [
        {
          id: 'first-time-user',
          title: 'Welcome! Your First Day',
          content: `
## 👋 Welcome to Yield Track Dashboard!

Don't worry if this looks complicated at first - we'll walk through everything step by step. Think of this system like a smart notebook that helps you track what's happening on your production floor.

### What This System Does (In Simple Terms)

Imagine you're running a factory that processes food (like chicken or vegetables). You need to know:
- **How much product comes IN** (we call this "infeed")
- **How much product goes OUT** (we call this "outfeed")
- **How well your workers are doing** (their productivity)
- **If you're making or losing product** (the "yield")

This system tracks all of that automatically!

[IMAGE: Dashboard Overview Screenshot - This is what you'll see when you first log in]

### The Basic Flow - Like Following a Recipe

1. **Raw product arrives** → Gets weighed and recorded
2. **Workers process it** → System tracks who's working where
3. **Finished product leaves** → Gets weighed again
4. **System calculates everything** → Shows you the results instantly

### Your Daily Routine Will Be:

**Morning:**
1. Log into the system (we'll show you how)
2. Check which workers showed up (in the Employees section)
3. Assign workers to their stations (like assigning seats in a classroom)
4. Start production for the day

**During the Day:**
1. Monitor the dashboard (it updates automatically)
2. Move workers around if needed
3. Check for any problems (shown in red)

**End of Day:**
1. Stop production
2. Generate your daily report
3. Log out

### Important Terms to Remember:

- **Station**: A specific workplace where one person works (like a desk)
- **Line**: A group of stations working together (like a classroom)
- **System**: Multiple lines working as one unit (like a whole school)
- **Shift**: The time period people work (morning shift, evening shift)
- **Yield**: How much product you get out compared to what went in (like getting 10 cookies from dough that could make 12)
          `
        },
        {
          id: 'logging-in',
          title: 'How to Log In (Step by Step)',
          content: `
## 🔐 Logging Into the System

Let's walk through logging in together. It's just like logging into your email or Facebook!

### Step 1: Open Your Web Browser

First, open your web browser. This could be:
- **Google Chrome** (colorful circle icon)
- **Microsoft Edge** (blue 'e' icon)
- **Firefox** (orange and blue icon)

[IMAGE: Browser Icons - Click on any of these icons on your computer]

### Step 2: Go to the Yield Track Website

In the address bar at the top (where you type website addresses), type:
- For local access: **http://localhost:5173**
- For network access: Ask your IT person for the address

[IMAGE: Address Bar Example - Type the address here and press Enter]

### Step 3: Enter Your Login Information

You'll see a login screen with two boxes:

**Username Box**: Type your username
- Administrator: Type **admin**
- Supervisor: Type **supervisor**
- Operator: Type **operator**

**Password Box**: Type your password
- For admin: Type **admin**
- For supervisor: Type **supervisor**
- For operator: Type **operator**

[IMAGE: Login Screen - Enter your username and password here]

### Step 4: Click the Login Button

After typing your username and password:
1. Click the blue **"Login"** button
2. Or press the **Enter** key on your keyboard

### What If Login Doesn't Work?

**Problem: "Login Failed" message**
- Check if you typed the username correctly (no spaces!)
- Check if you typed the password correctly (it's case-sensitive)
- Make sure Caps Lock is OFF

**Problem: Page won't load**
- Check if the server is running (ask IT)
- Check your internet connection
- Try refreshing the page (press F5)

### After Successful Login

You'll see the main dashboard with:
- A menu on the left side
- Production information in the center
- Your username in the top right corner

[IMAGE: Successful Login Dashboard - This is what you see after logging in successfully]

### Important Security Tips:

1. **Never share your password** with anyone
2. **Always log out** when you're done (click your name → Logout)
3. **Don't write passwords on sticky notes** near the computer
4. If you forget your password, contact your supervisor
          `
        },
        {
          id: 'understanding-the-screen',
          title: 'Understanding What You See on Screen',
          content: `
## 🖥️ Understanding Your Screen Layout

Let's break down what you're looking at - think of it like learning to read a car's dashboard.

### The Main Parts of Your Screen

[IMAGE: Screen Layout with Labels - The main areas of your screen]

### 1. Left Side Menu (Navigation Panel)

This is like the table of contents in a book. Each button takes you to a different section:

- **🏠 Dashboard**: The main overview (like your car's speedometer)
- **⚙️ Stations**: Where you assign workers to workplaces
- **👥 Employees**: List of all your workers
- **📦 Products**: What you're making
- **📊 Reports**: See past performance
- **🔧 Settings**: Change system options
- **📖 User Manual**: Help documentation (you're reading it now!)

**How to Use the Menu:**
1. Click on any menu item
2. The center of the screen changes to show that section
3. The selected item turns blue so you know where you are

### 2. Top Bar (Status Information)

This shows important "right now" information:

[IMAGE: Top Status Bar - Always visible status information]

- **Current Shift**: Shows if it's Shift 1 (morning) or Shift 2 (evening)
- **Time**: Current time
- **Production Status**: 
  - 🟢 Green = Running normally
  - 🟡 Yellow = Warning or standby
  - 🔴 Red = Stopped or problem
- **User Info**: Your username (click to logout)

### 3. Main Work Area (Center)

This is where you do your actual work. It changes based on what menu item you selected:

- **On Dashboard**: Shows production numbers and charts
- **On Stations**: Shows tables of workstations
- **On Employees**: Shows list of workers
- **And so on...**

### 4. Understanding Colors and What They Mean

The system uses colors like traffic lights:

**🟢 GREEN means GOOD:**
- Worker is active and producing
- System is running normally
- Target is being met

**🟡 YELLOW means CAUTION:**
- Worker is on break
- System is in standby
- Performance is below target but acceptable

**🔴 RED means ATTENTION NEEDED:**
- Worker absent or not working
- System has stopped
- Major problem detected

**⚫ GRAY means INACTIVE:**
- Station is disabled
- Worker not assigned
- Feature not in use

### 5. Common Buttons and What They Do

[IMAGE: Common Buttons - Buttons you'll use every day]

**➕ Add Button (Plus sign)**: Creates something new
- Add Employee = Add a new worker
- Add Product = Add a new product type

**✏️ Edit Button (Pencil)**: Changes existing information
- Click to modify details

**🗑️ Delete Button (Trash can)**: Removes something
- Be careful! Usually asks "Are you sure?"

**💾 Save Button**: Keeps your changes
- Always click this after making changes

**❌ Cancel Button**: Discards changes
- Use if you made a mistake

### 6. Tables and Lists

Many screens show information in tables (like a spreadsheet):

[IMAGE: Table Example - Information organized in rows and columns]

- **Headers** (top row): Tells you what each column means
- **Rows**: Each row is one item (one employee, one station, etc.)
- **Sorting**: Click column headers to sort A-Z or Z-A
- **Search Box**: Type to find specific items quickly

### Practice Exercise:

Try this to get comfortable:
1. Click on "Employees" in the left menu
2. Look at the table that appears
3. Find the search box and type a name
4. Click on "Dashboard" to go back
5. Notice how the screen changed each time

Remember: You can't break anything by clicking around and exploring!
          `
        }
      ]
    },
    'dashboard': {
      id: 'dashboard',
      title: '📊 Dashboard Guide',
      content: '',
      subsections: [
        {
          id: 'reading-the-dashboard',
          title: 'How to Read Your Dashboard',
          content: `
## 📊 Understanding Your Production Dashboard

The dashboard is like your car's instrument panel - it shows you everything happening right now. Let's learn to read it!

### The Big Picture - What Am I Looking At?

[IMAGE: Full Dashboard View - Your command center for production monitoring]

Think of the dashboard as answering these questions:
1. **How much are we producing?** (Total pounds/kilos)
2. **How fast are we working?** (Units per hour)
3. **Are we doing well?** (Yield percentage)
4. **Who's working where?** (Station assignments)

### Section 1: Production Status Bar (Top)

[IMAGE: Production Status Bar - Shows current production mode and shift]

This colorful bar at the top tells you:

**Current Mode** (with color coding):
- **🟢 PRODUCTION** (Green): Machines running, people working
- **🟡 STANDBY** (Yellow): Ready but not producing
- **🔴 STOP** (Red): Everything stopped
- **🟠 PREPARE** (Orange): Getting ready to start
- **🔵 CLEAN** (Blue): Cleaning/maintenance time

**Shift Information**:
- **Shift 1**: 6:00 AM - 2:00 PM (8 hours)
- **Shift 2**: 2:00 PM - 3:00 AM (13 hours)
- Shows which shift is currently active

### Section 2: The Three Key Numbers (Summary Cards)

[IMAGE: Three Summary Cards - Your most important metrics at a glance]

These three boxes show your KEY performance indicators:

**📦 Total System Output**
- **What it means**: Total pounds/kilos produced today
- **Good number**: Depends on your daily target
- **Example**: "1,234 lbs" means you've produced 1,234 pounds so far

**👤 Average Worker Output**
- **What it means**: How much each worker produced on average
- **How it's calculated**: Total output ÷ Number of workers
- **Good number**: Usually 100+ lbs per worker
- **Example**: "156 lbs" means each worker averaged 156 pounds

**📈 Average System Yield**
- **What it means**: Percentage of input that became sellable output
- **How it's calculated**: (Output ÷ Input) × 100
- **Good number**: Usually 85% or higher
- **Example**: "92%" means you kept 92% of what came in

### Section 3: Production Lines Overview

[IMAGE: Production Lines Cards - Each card represents one production line]

Each colored card represents one production line:

**Understanding Each Line Card:**
- **Line Name**: Like "Line 1" or "Packaging Line"
- **Status Light**: 
  - 🟢 Green dot = Running
  - 🔴 Red dot = Stopped
- **Workers**: Shows "5/8" meaning 5 workers assigned out of 8 stations
- **Current Product**: What's being processed
- **Yield**: Current yield percentage for this line

**How to Change Line Status:**
1. Click on the line card
2. Select new status from dropdown menu
3. Confirm your choice

### Section 4: Detailed Production Table

[IMAGE: Production Data Table - Detailed information for each station]

This table shows EVERY station's performance:

**Columns Explained (left to right):**
1. **Station**: Station number (like Station 1, Station 2)
2. **Employee**: Worker's name at that station
3. **Infeed**: Pounds/kilos that came IN to this station
4. **Outfeed**: Pounds/kilos that went OUT from this station
5. **Yield %**: (Outfeed ÷ Infeed) × 100
6. **PPH**: Pounds Per Hour (how fast they're working)
7. **Time**: How long they've been working
8. **Status**: Working, Break, or Not Working

### Reading the Table - What to Look For:

**🟢 Good Signs:**
- Yield % above 85%
- PPH steady or increasing
- All stations showing "Working"
- Numbers updating regularly

**🔴 Warning Signs:**
- Yield % below 80% (losing too much product)
- PPH dropping (slowing down)
- Multiple stations "Not Working"
- Numbers not updating (possible system issue)

### Quick Health Check - Is Everything OK?

Ask yourself these questions:
1. **Are all lines green?** If not, check what's wrong
2. **Is yield above target?** If not, investigate waste
3. **Are workers at their stations?** If not, reassign
4. **Are numbers updating?** If not, check system connection

### What to Do When Numbers Look Bad:

**Low Yield (Below 80%):**
1. Check if workers need training
2. Look for equipment problems
3. Verify product quality coming in
4. Check if scales are working correctly

**Low Production (PPH):**
1. Check if workers need help
2. Look for bottlenecks (one slow station)
3. Verify enough input material
4. Check equipment speed settings

### Pro Tips for Daily Monitoring:

1. **Check dashboard every hour** - Catch problems early
2. **Compare to yesterday** - Are we doing better or worse?
3. **Watch for patterns** - Does production drop after lunch?
4. **Set mental targets** - Know what "good" looks like
5. **Take screenshots** - Document good and bad days

Remember: The dashboard updates automatically every few seconds - you don't need to refresh!
          `
        }
      ]
    },
    'stations': {
      id: 'stations',
      title: '⚙️ Station Management',
      content: '',
      subsections: [
        {
          id: 'assigning-workers',
          title: 'How to Assign Workers to Stations',
          content: `
## 👥 Assigning Workers to Stations (Step-by-Step)

This is one of your most important daily tasks. Let's make it simple!

### Before You Start

[IMAGE: Stations Page Overview - The Stations management page]

**What You Need**:
1. ✅ Know which workers are present today
2. ✅ Know which stations need workers
3. ✅ Be in PREPARE or STANDBY mode (not required but recommended)

### Step 1: Go to Stations Page

1. Click **"Stations"** in the left menu
2. You'll see tabs for different areas:
   - System tabs (if you have multiple systems)
   - Line tabs (Line 1, Line 2, etc.)
   - Shift tabs (Shift 1, Shift 2)

### Step 2: Select the Right Location

[IMAGE: Tab Selection - Choose System → Line → Shift]

**Choose in Order**:
1. **System Tab**: Pick your system (if you have more than one)
2. **Line Tab**: Pick which line you're assigning
3. **Shift Tab**: Pick current shift
   - Shift 1 = Morning (6 AM - 2 PM)
   - Shift 2 = Evening (2 PM - 3 AM)

The active shift has a green dot!

### Step 3: Find an Empty Station

[IMAGE: Empty Station Row - Look for "Unassigned" in the Employee column]

Look for stations that show:
- **"Unassigned"** in the employee column
- Gray colored row (inactive)
- Status showing "Not Working"

### Step 4: Click to Assign

[IMAGE: Assignment Dialog Box - The employee selection window]

1. **Click** on "Unassigned" or the employee name field
2. A window pops up showing available workers
3. You'll see:
   - Worker names
   - Employee IDs
   - Who's already assigned (grayed out)

### Step 5: Choose Your Worker

**In the popup window**:

[IMAGE: Employee List - Available workers to choose from]

**Understanding the List**:
- ✅ **White rows**: Available workers
- ⛔ **Gray rows**: Already assigned elsewhere
- 🔵 **Blue highlight**: Currently selected

**Search Feature**:
- Type in the search box to find someone quickly
- Search by name OR employee ID
- Example: Type "John" to find John Smith

**Hide Assigned Workers** (Checkbox):
- ✅ Checked = Only shows available workers
- ⬜ Unchecked = Shows everyone (assigned ones are gray)

### Step 6: Make the Assignment

1. **Click** on the worker's name
2. They're automatically assigned!
3. Window closes
4. Station updates to show:
   - Worker's name
   - Status changes to "Working" (if in PRODUCTION mode)
   - Row color changes to white (active)

### Step 7: Verify Assignment

[IMAGE: Completed Assignment - Station now shows assigned worker]

Check that:
- ✅ Worker's name appears
- ✅ Station is no longer gray
- ✅ Can toggle Working/Not Working button

### How to Remove/Change Assignments

**To Remove a Worker**:
1. Click on the assigned worker's name
2. In the popup, click **"Unassign Employee"** button (red)
3. Station returns to "Unassigned"

**To Replace with Different Worker**:
1. Click on current worker's name
2. Select new worker from list
3. Automatically replaces old assignment

### Common Assignment Scenarios

**Scenario 1: Start of Shift**
1. Switch to PREPARE mode
2. Go through each line
3. Assign all workers
4. Switch to PRODUCTION mode

**Scenario 2: Worker Goes Home Sick**
1. Find their station
2. Click their name
3. Click "Unassign Employee"
4. Assign replacement worker

**Scenario 3: Moving Workers Around**
1. Unassign from old station first
2. Then assign to new station
3. (Can't be in two places at once!)

### Troubleshooting Assignment Issues

**"Can't assign worker"**:
- Check if they're already assigned elsewhere
- Verify they're scheduled for this shift
- Make sure station is enabled

**"Assignment disappeared"**:
- Check you're looking at right shift
- Verify you clicked Save (if required)
- Refresh page and check again

**"Wrong worker assigned"**:
- Simply click and reassign
- No need to unassign first

### Best Practices

**DO**:
- ✅ Assign workers before starting production
- ✅ Keep assignments updated when people move
- ✅ Check all lines are properly staffed
- ✅ Use search to find workers quickly

**DON'T**:
- ❌ Leave stations unassigned during production
- ❌ Assign one person to multiple stations
- ❌ Forget to unassign when workers leave

### Quick Assignment Checklist

Print this for daily use:

⬜ All present workers identified
⬜ Correct shift selected
⬜ Each line checked
⬜ All stations assigned
⬜ No duplicate assignments
⬜ Production mode activated

Remember: Good assignments = smooth production day!
          `
        }
      ]
    },
    'troubleshooting': {
      id: 'troubleshooting',
      title: '🔧 Troubleshooting',
      content: '',
      subsections: [
        {
          id: 'common-problems',
          title: 'Common Problems and Solutions',
          content: `
## 🔧 Fixing Common Problems

Don't panic! Most problems have simple solutions. Let's go through them together.

### Problem 1: "I Can't Log In!"

[IMAGE: Login Error Message - Common login error screen]

**Symptom**: Login button doesn't work or shows "Login Failed"

**Try These Solutions (in order)**:

1. **Check Your Typing**:
   - Username has no spaces (correct: "admin" wrong: "admin ")
   - Password is case-sensitive (Admin is different from admin)
   - Turn OFF Caps Lock key

2. **Check the Basics**:
   - Is the computer on? (sounds silly but check!)
   - Is internet connected? (look for WiFi symbol)
   - Is the server running? (ask IT)

3. **Try Default Passwords**:
   - Username: admin, Password: admin
   - Username: supervisor, Password: supervisor
   - Username: operator, Password: operator

4. **Clear Browser Cache**:
   - Press Ctrl+Shift+Delete
   - Select "Cached images and files"
   - Click "Clear data"
   - Try logging in again

**Still Not Working?**
- Contact your supervisor
- They might need to reset your password
- Server might be down

### Problem 2: "Numbers Aren't Updating!"

[IMAGE: Static Dashboard Numbers - Dashboard showing old data]

**Symptom**: Dashboard shows same numbers for minutes/hours

**Quick Checks**:

1. **Check Production Mode**:
   - Are you in PRODUCTION mode (green)?
   - STOP mode doesn't collect data!

2. **Check Internet Connection**:
   - Look for red warning messages
   - Try refreshing page (F5 key)

3. **Check Station Status**:
   - Are stations set to "Working"?
   - Unassigned stations don't produce data

**Solutions**:
- Refresh browser (F5 key)
- Check all workers are assigned
- Verify production mode is active
- Restart browser if needed

### Problem 3: "Can't Assign Workers!"

[IMAGE: Assignment Error - Unable to assign employee to station]

**Common Causes and Fixes**:

1. **Worker Already Assigned**:
   - Check if they're at another station
   - Unassign from old station first

2. **Wrong Shift Selected**:
   - Verify you're on correct shift tab
   - Check worker's shift assignment

3. **Station is Disabled**:
   - Enable the station first
   - Then try assigning

4. **Browser Issue**:
   - Try refreshing page
   - Click assignment again

### Problem 4: "Reports Are Empty!"

[IMAGE: Empty Report - Report showing no data]

**Why Reports Might Be Empty**:

1. **Wrong Date Range**:
   - Check start and end dates
   - Make sure data exists for those dates

2. **No Production That Day**:
   - Verify production actually happened
   - Check if system was in STOP all day

3. **Filters Too Restrictive**:
   - Remove filters one by one
   - Try "All" for each filter option

**Fix Steps**:
1. Set date to yesterday (you know had production)
2. Remove all filters
3. Click "Generate Report"
4. If works, slowly add filters back

### Problem 5: "System Is Slow!"

[IMAGE: Loading Spinner - System taking long time to respond]

**Speed Up Your System**:

1. **Close Unnecessary Tabs**:
   - Each browser tab uses memory
   - Keep only Yield Track open

2. **Clear Browser Data**:
   - Settings → Clear browsing data
   - Select last 7 days
   - Clear cookies and cache

3. **Check Internet Speed**:
   - Run speed test
   - Contact IT if slow

4. **Restart Computer**:
   - Sometimes the simplest solution!
   - Restart once per week minimum

### When to Call for Help

**Call IT When**:
- Same problem happens repeatedly
- Error messages you don't recognize
- System completely frozen
- Password needs reset

**Information to Provide**:
1. What you were trying to do
2. Exact error message
3. What time it happened
4. Screenshot if possible

Remember: Most problems are simple to fix - don't be afraid to try these solutions!
          `
        }
      ]
    }
  };

  useEffect(() => {
    // Check if there's a hash in the URL for direct linking
    const hash = window.location.hash.replace('#', '');
    if (hash && hash.startsWith('manual/')) {
      const section = hash.replace('manual/', '');
      const [category, subsection] = section.split('/');
      if (manualContent[category]) {
        setSelectedCategory(category);
        if (subsection) {
          setSelectedSection(subsection);
        }
      }
    }
  }, []);

  const renderContent = () => {
    const category = manualContent[selectedCategory];
    if (!category) return null;

    if (selectedSection) {
      const section = category.subsections?.find(s => s.id === selectedSection);
      if (section) {
        return (
          <div className="prose prose-slate max-w-none">
            <button
              onClick={() => setSelectedSection('')}
              className="mb-4 text-indigo-600 hover:text-indigo-700 flex items-center gap-2"
            >
              ← Back to {category.title}
            </button>
            <h1 className="text-3xl font-bold text-slate-800 mb-6">{section.title}</h1>
            <div 
              className="whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: formatContent(section.content) }}
            />
          </div>
        );
      }
    }

    return (
      <div>
        <h1 className="text-3xl font-bold text-slate-800 mb-2">{category.title}</h1>
        <p className="text-slate-600 mb-6">Choose a topic below to learn more:</p>
        {category.subsections && (
          <div className="grid gap-4">
            {category.subsections.map(subsection => (
              <div
                key={subsection.id}
                onClick={() => setSelectedSection(subsection.id)}
                className="bg-white p-6 rounded-lg border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer"
              >
                <h3 className="text-xl font-semibold text-slate-700 mb-2">{subsection.title}</h3>
                <p className="text-slate-600">
                  {getPreview(subsection.content)}
                </p>
                <span className="text-indigo-600 mt-2 inline-block">Read more →</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const formatContent = (content: string) => {
    // First handle image placeholders marked with [IMAGE: ...]
    let formatted = content.replace(/\[IMAGE: ([^\]]+)\]/g, (match, text) => {
      const parts = text.split(' - ');
      const alt = parts[0] || '';
      const caption = parts[1] || '';
      
      return createImagePlaceholder(alt, caption);
    });

    // Then apply markdown formatting
    return formatted
      .replace(/### (.*)/g, '<h3 class="text-xl font-semibold text-slate-700 mt-6 mb-3">$1</h3>')
      .replace(/## (.*)/g, '<h2 class="text-2xl font-bold text-slate-800 mt-8 mb-4">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
      .replace(/- (.*)/g, '<li class="ml-6 mb-2 text-slate-700">$1</li>')
      .replace(/\d+\.\s+(.*)/g, '<li class="ml-6 mb-2 text-slate-700">$1</li>')
      .replace(/```([^`]+)```/g, '<pre class="bg-slate-100 p-4 rounded-lg overflow-x-auto"><code class="text-sm">$1</code></pre>')
      .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-2 py-1 rounded text-sm">$1</code>')
      .replace(/⬜/g, '<span class="text-lg">☐</span>')
      .replace(/✅/g, '<span class="text-green-600 text-lg">✓</span>')
      .replace(/❌/g, '<span class="text-red-600 text-lg">✗</span>')
      .replace(/🟢/g, '<span class="text-green-600 text-xl">●</span>')
      .replace(/🟡/g, '<span class="text-yellow-500 text-xl">●</span>')
      .replace(/🔴/g, '<span class="text-red-600 text-xl">●</span>')
      .replace(/🔵/g, '<span class="text-blue-600 text-xl">●</span>')
      .replace(/🟠/g, '<span class="text-orange-500 text-xl">●</span>')
      .replace(/⚫/g, '<span class="text-gray-600 text-xl">●</span>')
      .replace(/\n\n/g, '</p><p class="mb-4 text-slate-700 leading-relaxed">')
      .replace(/^/, '<p class="mb-4 text-slate-700 leading-relaxed">')
      .replace(/$/, '</p>');
  };

  const getPreview = (content: string) => {
    const lines = content.trim().split('\n').filter(line => line.trim() && !line.startsWith('#') && !line.includes('[IMAGE:'));
    const preview = lines[0]?.substring(0, 200) || '';
    return preview + (preview.length >= 200 ? '...' : '');
  };

  return (
    <div className="flex h-full bg-slate-50">
      {/* Sidebar */}
      <div className="w-72 bg-white border-r border-slate-200 overflow-y-auto">
        <div className="p-4">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            📚 User Documentation
          </h2>
          <nav className="space-y-1">
            {Object.values(manualContent).map(category => (
              <div key={category.id}>
                <button
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setSelectedSection('');
                    toggleSection(category.id);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center justify-between ${
                    selectedCategory === category.id
                      ? 'bg-indigo-50 text-indigo-700 font-medium'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span>{category.title}</span>
                  {category.subsections && category.subsections.length > 0 && (
                    <svg
                      className={`w-4 h-4 transition-transform ${
                        expandedSections.has(category.id) ? 'rotate-90' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
                {expandedSections.has(category.id) && category.subsections && (
                  <div className="ml-4 mt-1 space-y-1">
                    {category.subsections.map(subsection => (
                      <button
                        key={subsection.id}
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setSelectedSection(subsection.id);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${
                          selectedSection === subsection.id
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {subsection.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">💡 Quick Tip</h3>
            <p className="text-sm text-blue-700">
              Click the ? icons throughout the application for instant help on specific features!
            </p>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-5xl">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default UserManualEnhanced;